/**
 * DealsCalendarPage — Marketing → Managed Deals Calendar (Phase 1 MVP)
 *
 * Dashboard with summary cards, filters, and a list of deals.
 * From here users can:
 *   - Start a new AI Planning Run (wizard)
 *   - Create a manual deal
 *   - Open a deal's detail page
 *   - Jump to the Deal Library
 */
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Megaphone, Sparkles, Plus, BookOpen, Filter,
  Rocket, Lightbulb, CheckCircle2, PauseCircle, Trophy,
  AlertTriangle, Clock, Layers,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import PlanningRunWizard from '../components/deals/PlanningRunWizard';
import SuggestedDealsReviewSheet from '../components/deals/SuggestedDealsReviewSheet';
import ManualDealDialog from '../components/deals/ManualDealDialog';
import DealCard from '../components/deals/DealCard';

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'ai_suggested', label: 'AI Suggested — Needs Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'live', label: 'Live' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'archived', label: 'Archived' },
];

const TYPE_FILTERS = [
  { value: 'all', label: 'All types' },
  { value: 'footfall_filler', label: 'Footfall Filler' },
  { value: 'basket_builder', label: 'Basket Builder' },
  { value: 'community_event', label: 'Community Event' },
  { value: 'local_group', label: 'Local Group' },
  { value: 'waste_rescue', label: 'Waste Rescue' },
  { value: 'repeat_visit', label: 'Repeat Visit' },
  { value: 'weather_triggered', label: 'Weather Triggered' },
  { value: 'seasonal_promotion', label: 'Seasonal' },
];

const SOURCE_FILTERS = [
  { value: 'all', label: 'All sources' },
  { value: 'ai', label: 'AI generated' },
  { value: 'manual', label: 'Manually created' },
];

export default function DealsCalendarPage() {
  const { api, currentLocation } = useAuth();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  const [wizardOpen, setWizardOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [reviewRunId, setReviewRunId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.deal_type = typeFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      const [sumRes, listRes] = await Promise.all([
        api.get('/deals/summary'),
        api.get('/deals', { params }),
      ]);
      setSummary(sumRes.data);
      setDeals(listRes.data.deals || []);
    } catch (e) {
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [statusFilter, typeFilter, sourceFilter]);

  const cards = useMemo(() => {
    if (!summary) return [];
    const c = summary.counts_by_status || {};
    return [
      { key: 'suggested', label: 'Needs Review', value: summary.needs_review || 0, icon: Lightbulb, color: 'text-amber-600 bg-amber-50 border-amber-200' },
      { key: 'approved', label: 'Approved', value: c.approved || 0, icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
      { key: 'scheduled', label: 'Scheduled', value: c.scheduled || 0, icon: Clock, color: 'text-blue-600 bg-blue-50 border-blue-200' },
      { key: 'live', label: 'Live', value: c.live || 0, icon: Rocket, color: 'text-pink-600 bg-pink-50 border-pink-200' },
      { key: 'paused', label: 'Paused', value: c.paused || 0, icon: PauseCircle, color: 'text-gray-600 bg-gray-50 border-gray-200' },
      { key: 'completed', label: 'Completed', value: c.completed || 0, icon: Trophy, color: 'text-purple-600 bg-purple-50 border-purple-200' },
      { key: 'attention', label: 'Attention', value: (summary.performance?.needs_attention || 0) + (summary.performance?.underperforming || 0), icon: AlertTriangle, color: 'text-red-600 bg-red-50 border-red-200' },
    ];
  }, [summary]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="deals-calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Megaphone className="w-4 h-4" /> Marketing
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold font-heading mt-1">Managed Deals Calendar</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Plan, approve, launch and review structured promotions — with an AI co-pilot.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/marketing/deals-calendar/library')}
            data-testid="deal-library-btn"
          >
            <BookOpen className="w-4 h-4 mr-2" /> Deal Library
          </Button>
          <Button
            variant="outline"
            onClick={() => setManualOpen(true)}
            data-testid="new-manual-deal-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Manual Deal
          </Button>
          <Button
            onClick={() => setWizardOpen(true)}
            data-testid="new-planning-run-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" /> New Planning Run
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {cards.map((c) => (
          <Card key={c.key} className={`border ${c.color}`} data-testid={`summary-card-${c.key}`}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <c.icon className="w-4 h-4" />
                <span className="text-2xl font-bold" data-testid={`summary-value-${c.key}`}>{c.value}</span>
              </div>
              <div className="text-xs mt-1">{c.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Filter className="w-4 h-4" /> Filters
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-56" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-56" data-testid="filter-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_FILTERS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-40" data-testid="filter-source">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SOURCE_FILTERS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto text-sm text-muted-foreground">
            {loading ? 'Loading…' : `${deals.length} deal${deals.length === 1 ? '' : 's'}`}
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {deals.length === 0 && !loading ? (
        <Card>
          <CardContent className="p-10 text-center">
            <Layers className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <div className="font-medium mb-1">No deals here yet</div>
            <p className="text-sm text-muted-foreground mb-4">
              Start with an AI-assisted planning run to get 3-5 tailored deal suggestions, or create one manually.
            </p>
            <div className="flex justify-center gap-2">
              <Button onClick={() => setWizardOpen(true)} data-testid="empty-start-planning-btn">
                <Sparkles className="w-4 h-4 mr-2" /> Start Planning Run
              </Button>
              <Button variant="outline" onClick={() => setManualOpen(true)}>
                <Plus className="w-4 h-4 mr-2" /> Add Manual Deal
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {deals.map((d) => (
            <DealCard
              key={d.id}
              deal={d}
              onClick={() => navigate(`/marketing/deals-calendar/${d.id}`)}
              data-testid={`deal-card-${d.id}`}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <PlanningRunWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        locationId={currentLocation?.id}
        onGenerated={(runId) => {
          setWizardOpen(false);
          setReviewRunId(runId);
        }}
      />
      <ManualDealDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        locationId={currentLocation?.id}
        onCreated={reload}
      />
      {reviewRunId && (
        <SuggestedDealsReviewSheet
          runId={reviewRunId}
          open={!!reviewRunId}
          onOpenChange={(v) => { if (!v) { setReviewRunId(null); reload(); } }}
        />
      )}
    </div>
  );
}
